import { useMemo } from 'react'
import { timestampDate } from '@bufbuild/protobuf/wkt'

import { formatTimestamp } from '@modules/utils/dateUtils'

import {
  ColorSimulationImageCreatedByType,
  type GetDealWithColorSimulationResponse,
} from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'
import type { SimulationRole } from '@ai-color-modules/permissions'
import { filterVisibleSimulatedImages } from '@ai-color-modules/utils/imageFilters'

import { getProcessedImageSelectionState } from '../utils/selectionState'

type TabItem = {
  value: string
  index: number
}

export type ThumbnailItem = {
  key: string
  imageId?: number
  url: string
  label?: string
  isActive: boolean
  tabValue?: string
  type: 'original' | 'processed'
  processedIndex?: number
  createdBy?: string
  createdAt?: Date
  categoryTag?: string
  createdByType?: ColorSimulationImageCreatedByType
  favorited?: boolean
  canDelete?: boolean
  /** 履歴のタイトル（未設定は空文字列）。既定表示は utils/historyDisplay で組み立てる */
  title?: string
  /** ピン留めした日時。undefined=ピン留めなし。昇順がピン留めした順 */
  pinnedAt?: Date
}

export type ThumbnailGroup = {
  key: string
  tabValue: string
  title: string
  date: string
  items: ThumbnailItem[]
}

interface UseThumbnailGroupsOptions {
  images: GetDealWithColorSimulationResponse['images']
  tabs: TabItem[]
  activeTab: string
  selectedProcessedIndexByTab: Record<string, number>
  staffName?: string
  customerName?: string
  customerNameFull?: string
  /** 見ている側のロール。施主画面では施主自身が作った履歴の作成者を「あなた」と出す（design.md D7） */
  viewerRole?: SimulationRole
}

export const useThumbnailGroups = ({
  images,
  tabs,
  activeTab,
  selectedProcessedIndexByTab,
  staffName,
  customerName,
  customerNameFull,
  viewerRole = 'company',
}: UseThumbnailGroupsOptions) => {
  return useMemo<ThumbnailGroup[]>(() => {
    const groups: ThumbnailGroup[] = []
    const customerDisplayName =
      viewerRole === 'customer' ? 'あなた' : customerNameFull || customerName

    images.forEach((image, index) => {
      const tab = tabs[index]
      if (!tab) {
        return
      }

      const tabValue = tab.value
      const processedImages = filterVisibleSimulatedImages(image?.simulatedImage)
      const { selectedProcessedIndex, originalIsActive } = getProcessedImageSelectionState(
        tabValue,
        processedImages.length,
        selectedProcessedIndexByTab,
      )

      const items: ThumbnailItem[] = [
        {
          key: `${tabValue}-original`,
          imageId: image.id ? Number(image.id) : undefined,
          url: image.url,
          isActive: tabValue === activeTab && originalIsActive,
          tabValue,
          type: 'original',
          createdBy: image.createdByName || staffName,
          createdAt: image.uploadedAt ? timestampDate(image.uploadedAt) : undefined,
          categoryTag: '現況',
          createdByType: ColorSimulationImageCreatedByType.STAFF,
          favorited: Boolean(image.favorited),
          canDelete: false,
        },
      ]

      processedImages.forEach((simulatedImage, simulatedIndex) => {
        if (!simulatedImage.url) {
          return
        }

        const createdByType =
          simulatedImage.createdByType ?? ColorSimulationImageCreatedByType.UNSPECIFIED
        const isCustomerCreated = createdByType === ColorSimulationImageCreatedByType.CUSTOMER

        items.push({
          key: `${tabValue}-simulated-${simulatedIndex}`,
          imageId: simulatedImage.id ? Number(simulatedImage.id) : undefined,
          url: simulatedImage.url,
          isActive: tabValue === activeTab && selectedProcessedIndex === simulatedIndex,
          tabValue,
          type: 'processed',
          processedIndex: simulatedIndex,
          createdBy: isCustomerCreated
            ? customerDisplayName
            : simulatedImage.createdByName || staffName,
          createdAt: simulatedImage.uploadedAt
            ? timestampDate(simulatedImage.uploadedAt)
            : undefined,
          categoryTag: isCustomerCreated ? '施主' : '生成',
          createdByType,
          favorited: Boolean(simulatedImage.favorited),
          canDelete: true,
          title: simulatedImage.title ?? '',
          pinnedAt: simulatedImage.pinnedAt ? timestampDate(simulatedImage.pinnedAt) : undefined,
        })
      })

      groups.push({
        key: tabValue,
        tabValue,
        title: `外観カット${index + 1}`,
        date: formatTimestamp(image.uploadedAt ? timestampDate(image.uploadedAt) : undefined),
        items,
      })
    })

    return groups
  }, [
    activeTab,
    customerName,
    customerNameFull,
    images,
    selectedProcessedIndexByTab,
    staffName,
    tabs,
    viewerRole,
  ])
}
